import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './InlineRename.css';

export default function InlineRename({ value, onSave, className = '' }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [note, setNote] = useState(null);
    const inputRef = useRef(null);
    const noteTimer = useRef(null);
    const cancelRef = useRef(false);

    useEffect(() => {
        if (!editing) {
            setDraft(value || '');
        }
    }, [value, editing]);

    useEffect(() => () => clearTimeout(noteTimer.current), []);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const stop = (event) => {
        event.stopPropagation();
    };

    const showSaved = () => {
        setNote({ type: 'saved', text: 'Saved' });
        clearTimeout(noteTimer.current);
        noteTimer.current = setTimeout(() => setNote(null), 2000);
    };

    const commit = () => {
        if (cancelRef.current) {
            cancelRef.current = false;
            return;
        }
        const next = draft.trim();
        if (!next) {
            setDraft(value || '');
            setEditing(false);
            setNote({ type: 'error', text: 'Name cannot be empty' });
            return;
        }
        if (next === (value || '').trim()) {
            setEditing(false);
            return;
        }
        setEditing(false);
        setNote(null);
        onSave(next, {
            onSuccess: showSaved,
            onError: () => setNote({ type: 'error', text: 'Could not save name' }),
        });
    };

    return (
        <span className={`inline-rename ${className}`.trim()} onClick={stop} onMouseDown={stop}>
            {editing ? (
                <input
                    ref={inputRef}
                    className="inline-rename-input"
                    value={draft}
                    maxLength={116}
                    aria-label="Name"
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={commit}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            event.currentTarget.blur();
                        } else if (event.key === 'Escape') {
                            cancelRef.current = true;
                            setDraft(value || '');
                            setEditing(false);
                        }
                    }}
                />
            ) : (
                <span className="inline-rename-label">{value || 'Untitled'}</span>
            )}
            {!editing && (
                <button
                    type="button"
                    className="inline-rename-pencil"
                    aria-label="Rename"
                    onClick={() => {
                        setNote(null);
                        setEditing(true);
                    }}
                >
                    <i className="ri-pencil-line" aria-hidden="true" />
                </button>
            )}
            {note && (
                <span className={`inline-rename-note ${note.type}`}>{note.text}</span>
            )}
        </span>
    );
}

InlineRename.propTypes = {
    value: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    className: PropTypes.string,
};
